import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";

// Pages
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Search from "./pages/Search";
import ListingDetail from "./pages/ListingDetail";
import Trips from "./pages/Trips";
import Messages from "./pages/Messages";

// Companion Pages
import HowItWorks from "./pages/HowItWorks";
import About from "./pages/About";
import HelpCenter from "./pages/HelpCenter";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";

// Host Pages
import HostDashboard from "./pages/host/Dashboard";
import HostListings from "./pages/host/Listings";
import CreateListing from "./pages/host/CreateListing";
import HostRequests from "./pages/host/Requests";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminListings from "./pages/admin/Listings";
import AdminUsers from "./pages/admin/Users";
import AdminDisputes from "./pages/admin/Disputes";

// Protected Route Components
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const GuestRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect based on role
    if (user?.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    if (user?.role === 'host') {
      return <Navigate to="/host/dashboard" replace />;
    }
    return <Navigate to="/search" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={
        <GuestRoute>
          <Auth />
        </GuestRoute>
      } />
      <Route path="/search" element={
        <Layout>
          <Search />
        </Layout>
      } />
      <Route path="/listings/:id" element={
        <Layout>
          <ListingDetail />
        </Layout>
      } />

      {/* Companion Website Routes */}
      <Route path="/how-it-works" element={
        <Layout>
          <HowItWorks />
        </Layout>
      } />
      <Route path="/about" element={
        <Layout>
          <About />
        </Layout>
      } />
      <Route path="/help" element={
        <Layout>
          <HelpCenter />
        </Layout>
      } />
      <Route path="/contact" element={
        <Layout>
          <Contact />
        </Layout>
      } />
      <Route path="/blog" element={
        <Layout>
          <Blog />
        </Layout>
      } />
      <Route path="/blog/:slug" element={
        <Layout>
          <BlogPost />
        </Layout>
      } />

      {/* Guest/User Protected Routes */}
      <Route path="/trips" element={
        <ProtectedRoute allowedRoles={['guest', 'host', 'admin']}>
          <Layout>
            <Trips />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute allowedRoles={['guest', 'host', 'admin']}>
          <Layout>
            <Messages />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Host Protected Routes */}
      <Route path="/host/dashboard" element={
        <ProtectedRoute allowedRoles={['host', 'admin']}>
          <Layout>
            <HostDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/host/listings" element={
        <ProtectedRoute allowedRoles={['host', 'admin']}>
          <Layout>
            <HostListings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/host/listings/new" element={
        <ProtectedRoute allowedRoles={['host', 'admin']}>
          <Layout>
            <CreateListing />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/host/requests" element={
        <ProtectedRoute allowedRoles={['host', 'admin']}>
          <Layout>
            <HostRequests />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Admin Protected Routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/listings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminListings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminUsers />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/disputes" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminDisputes />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#292524',
              color: '#E7E5E4',
              border: '1px solid #44403C',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
